import React, { useEffect, useState } from 'react';
import { Button, Form } from 'antd';
import { useAtomValue } from 'jotai';
import { Header } from './header';
import { FooterWrapper, StyledDrawer } from './styled';
import EmailSubscriptionsList from './list';
import { createSubscriptionAtom, editSubscriptionAtom, isEditCreateFormOpenAtom } from './atoms';
import EmailSubscriptionEdit from './edit';
import { useCreateSubscription, useEditSubscription } from './edit/hooks';
import { getESMetricSeverityMap, normalizeEmails } from './utils';
import { useResetPanelAtoms } from './hooks';
import { useSelector } from 'react-redux';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import apptracker from '@src/utils/sensor/apptracker';
import { CREATE_NEW_EMAIL_SUBSCRIPTIONS, UPDATE_EMAIL_SUBSCRIPTION } from '@src/utils/sensor/track';
import { MetricConfig } from './types';

const EmailSubscriptionsDrawer = ({ isOpen, closeDrawer }) => {
  const isAdminUser = useSelector(isAdminRoleSelector);
  const editSubscription = useAtomValue(editSubscriptionAtom);
  const createSubscription = useAtomValue(createSubscriptionAtom);
  const isEditCreateFormOpen = useAtomValue(isEditCreateFormOpenAtom);
  const resetAtoms = useResetPanelAtoms();
  const [subscriptionForm] = Form.useForm();
  const { mutate: onCreate, isLoading: createLoading } = useCreateSubscription();
  const { mutate: onEdit, isLoading: editLoading } = useEditSubscription();
  const [canSubmit, setCanSubmit] = useState(false);

  // TODO-update-antd: In version 5.4.4 and above preserve key helps getting unresistered form fields
  const formFieldValues = Form.useWatch([], subscriptionForm);

  const handleCloseDrawer = () => {
    resetAtoms.reset();
    closeDrawer();
  };

  const handleCloseForm = () => {
    resetAtoms.reset();
  };

  const onSave = () => {
    const paramMap = getESMetricSeverityMap(formFieldValues['metricList']);
    const emails = normalizeEmails(formFieldValues['emailArray']);
    onCreate({
      emails,
      metrics: paramMap.aiAlertMetrics,
      autoInsights: paramMap.autoInsightItems,
      hidden: false,
    });
    apptracker(CREATE_NEW_EMAIL_SUBSCRIPTIONS, {
      emails,
      metrics: paramMap.aiAlertMetrics,
      autoInsights: paramMap.autoInsightItems,
      hidden: false,
    });
  };

  const onUpdate = () => {
    const paramMap = getESMetricSeverityMap(formFieldValues['metricList']);
    const emails = normalizeEmails(formFieldValues['emailArray']);
    onEdit({
      emails,
      metrics: paramMap.aiAlertMetrics,
      autoInsights: paramMap.autoInsightItems,
      hidden: false,
    });
    apptracker(UPDATE_EMAIL_SUBSCRIPTION, {
      emailId: editSubscription.emailId,
      data: {
        emails: [editSubscription.emailId || ''],
        metrics: paramMap.aiAlertMetrics,
        autoInsights: paramMap.autoInsightItems,
        hidden: false,
      },
    });
  };

  useEffect(() => {
    subscriptionForm
      .validateFields(['emailArray', 'metricList'])
      .then((values: Record<string, MetricConfig>) => {
        const hasData = Object.values(values['metricList']).some((value) => {
          return value?.severity || value?.option;
        });
        setCanSubmit(hasData);
      })
      .catch(() => setCanSubmit(false));
  }, [subscriptionForm, formFieldValues, canSubmit]);

  return (
    <StyledDrawer
      open={isOpen}
      closeIcon={null}
      styles={{
        wrapper: {
          width: '85%',
          minWidth: '800px',
        },
      }}
      onClose={handleCloseDrawer}
      keyboard={true}
      footer={
        <FooterWrapper>
          {isEditCreateFormOpen ? (
            <Button onClick={handleCloseForm}>{isAdminUser ? 'Cancel' : 'Back'}</Button>
          ) : null}
          {isAdminUser && !!editSubscription.emailId ? (
            <Button type="primary" ghost onClick={onUpdate} disabled={!canSubmit}>
              Update
            </Button>
          ) : null}
          {isAdminUser && createSubscription ? (
            <Button type="primary" ghost onClick={onSave} disabled={!canSubmit}>
              Save
            </Button>
          ) : null}
        </FooterWrapper>
      }
    >
      <Header closeDrawer={handleCloseDrawer} />

      {!isEditCreateFormOpen && isOpen ? <EmailSubscriptionsList isDrawerOpen={isOpen} /> : null}

      {isEditCreateFormOpen ? (
        <EmailSubscriptionEdit
          emailId={editSubscription.emailId}
          createEditLoading={createLoading || editLoading}
          form={subscriptionForm}
        />
      ) : null}
    </StyledDrawer>
  );
};

export default EmailSubscriptionsDrawer;
